import * as Sentry from '@sentry/react';

type SentryBrowserInitOptions = {
  dsn?: string;
  environment?: string;
  release?: string;
  tracesSampleRate?: number;
};

let enabled = false;

type ViteEnv = Record<string, string | boolean | undefined>;

function getViteEnv(): ViteEnv {
  return (import.meta as ImportMeta & { env?: ViteEnv }).env ?? {};
}

function readViteEnv(): SentryBrowserInitOptions {
  const env = getViteEnv();
  const stage = typeof env.VITE_STAGE === 'string' ? env.VITE_STAGE.trim() : '';
  return {
    dsn: typeof env.VITE_SENTRY_DSN === 'string' ? env.VITE_SENTRY_DSN.trim() : undefined,
    environment: stage || (env.PROD ? 'production' : 'development'),
    release:
      (typeof env.VITE_GIT_COMMIT === 'string' && env.VITE_GIT_COMMIT.trim()) ||
      (typeof env.VITE_APP_VERSION === 'string' && env.VITE_APP_VERSION.trim()) ||
      undefined,
  };
}

/** Инициализация Sentry для Vite SPA. Без VITE_SENTRY_DSN — no-op. */
export function initSentryBrowser(overrides?: SentryBrowserInitOptions): boolean {
  if (enabled) return true;

  const options = { ...readViteEnv(), ...overrides };
  if (!options.dsn) return false;

  Sentry.init({
    dsn: options.dsn,
    environment: options.environment,
    release: options.release,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate:
      options.tracesSampleRate ?? (getViteEnv().PROD ? 0.1 : 1),
  });

  enabled = true;
  return true;
}

export function isSentryBrowserEnabled(): boolean {
  return enabled;
}

export function captureBrowserException(
  error: unknown,
  context?: { componentStack?: string | null; tags?: Record<string, string> }
): void {
  if (!enabled) return;

  Sentry.withScope((scope) => {
    if (context?.tags) {
      for (const [key, value] of Object.entries(context.tags)) {
        scope.setTag(key, value);
      }
    }
    if (context?.componentStack) {
      scope.setContext('react', { componentStack: context.componentStack });
    }
    Sentry.captureException(error);
  });
}
